import { FC, ReactNode } from "react"
import clsx from "clsx"

export interface Tab {
  key: string
  label: string | ReactNode
}

export type TabVariant = "underline" | "pills"

interface TabsProps {
  tabs: Tab[]
  activeTab: string
  onChange: (key: string) => void
  variant?: TabVariant
}

export const Tabs: FC<TabsProps> = ({ tabs, activeTab, onChange, variant = "underline" }) => {
  if (variant === "pills") {
    return (
      // Scrolls rather than wraps: a wrapped row eats the viewport on a phone.
      // `min-w-0` so the row shrinks when it sits beside something in a flex header —
      // otherwise it holds all its pills at full width and the page scrolls, not the row.
      <div className="tabs min-w-0 max-w-full flex-nowrap gap-2 overflow-x-auto">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.key

          return (
            <a
              key={tab.key}
              onClick={() => onChange(tab.key)}
              className={clsx(
                "flex shrink-0 cursor-pointer items-center text-left py-3 rounded-lg px-4 text-base border transition-all whitespace-nowrap",
                "text-[rgba(6,7,21,1)] dark:text-white",
                isActive
                  ? "text-white bg-[rgba(132,0,168,1)] border-[rgba(132,0,168,1)]"
                  : "bg-white dark:bg-[#0C0C1A] border-[rgba(132,0,168,1)] dark:border-transparent hover:border-[rgba(132,0,168,0.8)] hover:bg-[rgba(169,78,206,0.2)] dark:hover:bg-[rgba(169,78,206,0.15)]"
              )}
            >
              {tab.label}
            </a>
          )
        })}
      </div>
    )
  }

  return (
    <div className="flex min-w-0 max-w-full items-center overflow-x-auto border-b border-[rgba(0,0,0,0.1)] dark:border-[rgba(255,255,255,0.1)]">
      {tabs.map((tab) => {
        const isActive = activeTab === tab.key

        return (
          <button
            key={tab.key}
            onClick={() => onChange(tab.key)}
            className={clsx(
              "shrink-0 whitespace-nowrap px-5 py-3 text-sm font-medium border-b-2 transition-all -mb-px",
              isActive
                ? "border-[rgba(139,92,246,1)] text-[rgba(139,92,246,1)]"
                : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            )}
          >
            {tab.label}
          </button>
        )
      })}
    </div>
  )
}
